import { t } from "@web-monorepo/i18n/macro";
import { i18n } from "@web-monorepo/i18n";
import * as logClient from "@classdojo/log-client";
import { useEffect, useRef, useState, useMemo } from "react";

type AudioProps = {
  src: string;
  isPlaying: boolean;
  onPlaying?: VoidFunction;
  onPause?: VoidFunction;
  onEnded?: VoidFunction;
  caption?: string;
};

const Audio = (props: AudioProps) => {
  const { src, isPlaying, onPlaying, onPause, onEnded, caption } = props;
  const audioNode = useRef<HTMLAudioElement | null>(null);
  const [songSrc, setSongSrc] = useState(src);

  const vttPath = useMemo(() => {
    if (caption) {
      // Create a VTT file with the caption
      const vttContent = `WEBVTT\n\n00:00:00.000 --> 99:00:00.000\n${caption}\n\n`;
      const vttBlob = new Blob([vttContent], { type: "text/vtt" });
      const vttBlobUrl = URL.createObjectURL(vttBlob);

      return vttBlobUrl;
    }

    return null;
  }, [caption]);

  // Clean up blob URL to prevent memory leak
  useEffect(() => {
    return () => {
      if (vttPath) {
        URL.revokeObjectURL(vttPath);
      }
    };
  }, [vttPath]);

  useEffect(() => {
    const isSameSong = songSrc === src;

    if (audioNode.current && isSameSong) {
      if (isPlaying) {
        audioNode.current.play().catch(() => {
          logClient.sendMetrics([
            {
              type: "increment",
              value: 1,
              metricName: "toolkit.media.audio.play.error",
            },
          ]);
        });
      } else {
        audioNode.current.pause();
      }
    } else {
      // eslint-disable-next-line @web-monorepo/no-setState-in-useEffect
      setSongSrc(src);
    }
  }, [isPlaying, songSrc, src]);

  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption -- decorative audio for classroom radio
    <audio src={src} autoPlay={isPlaying} ref={audioNode} onPlaying={onPlaying} onPause={onPause} onEnded={onEnded}>
      {caption && vttPath && <track kind="captions" src={vttPath} srcLang={i18n.locale} label={t`Captions`} />}
    </audio>
  );
};

export default Audio;
